import { MessagePrimitive, useAuiState, useMessagePartText } from '@assistant-ui/react';
import clsx from 'clsx';

import { Markdown } from '#components/markdown/markdown.js';
import { UserAvatar } from '#components/user-avatar/user-avatar.js';
import { useAttachmentStore } from '../attachment-store-context.js';
import { SentAttachment } from './sent-attachment.js';

import type { UserAvatarUser } from '#components/user-avatar/user-avatar.js';

function TextPart() {
	const part = useMessagePartText();
	return <Markdown>{part.text}</Markdown>;
}

export interface UserMessageProps {
	/** Conversation owner shown for messages that were not sent by the current user. */
	author?: UserAvatarUser;
}

/** Renders a user message with either the current-user label or the supplied owner's avatar. */
export function UserMessage({ author }: UserMessageProps) {
	const messageId = useAuiState((s) => s.message.id);

	const sentAttachments = useAttachmentStore((s) => s.sent.get(messageId)) ?? [];

	return (
		<MessagePrimitive.Root
			className={clsx(
				'mb-4 grid grid-cols-[46px_1fr] gap-x-2.5',
				sentAttachments.length > 0 || author ? 'items-start' : 'items-baseline',
			)}
		>
			{author ?
				<div className='flex justify-end select-none'>
					<UserAvatar user={author} size='xs' />
				</div>
			:	<span className='text-right font-mono text-sm text-gray-a9 select-none'>you ❯</span>}
			<div className='min-w-0 text-sm leading-relaxed text-gray-12'>
				{sentAttachments.length > 0 && (
					<div className='mb-2 flex flex-wrap gap-1'>
						{sentAttachments.map((att) => (
							<SentAttachment key={att.id} attachment={att} />
						))}
					</div>
				)}
				<MessagePrimitive.Parts components={{ Text: TextPart }} />
			</div>
		</MessagePrimitive.Root>
	);
}
